import { useState } from "react"
import { useUploadImageMutation } from "../../API/authApi";
import { ImageTypes } from "../../Interfaces/componentTypes";
import { useToastify } from "../../Hooks/hooks";
import useLanguagesHooks from "../Languages/setLanguage-hooks";

const MAX_UPLOAD_BYTES = 900 * 1024; // Keep payload safely under stricter server limits.
const RESIZE_STEP = 0.85;
const MAX_RESIZE_ATTEMPTS = 10;

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
};

const useUploadFile = (type: ImageTypes) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadImage, { data: ImageResponce, isLoading: imageLoading, isSuccess: imageLoadingSuccess, isError: imageLoadingError }] = useUploadImageMutation();
  const { showToastError } = useToastify();
  const { t } = useLanguagesHooks();

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (file) {
        const img = new Image();
        const fileUrl = URL.createObjectURL(file);
        img.src = fileUrl;
        img.onload = async () => {
          const maxWidth = 800;  
          const maxHeight = 1000; 
          let newWidth = img.width;
          let newHeight = img.height;
          if (newWidth > maxWidth || newHeight > maxHeight) {
            const aspectRatio = img.width / img.height;
            if (newWidth > maxWidth) {
              newWidth = maxWidth;
              newHeight = newWidth / aspectRatio;
            }
            if (newHeight > maxHeight) {
              newHeight = maxHeight;
              newWidth = newHeight * aspectRatio;
            }
          }

          let width = Math.round(newWidth);
          let height = Math.round(newHeight);
          let bestBlob: Blob | null = null;
          let bestMimeType = file.type;
          const isLossyFormat = file.type === "image/jpeg" || file.type === "image/webp";

          for (let attempt = 0; attempt < MAX_RESIZE_ATTEMPTS; attempt++) {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            if (!ctx) break;
            ctx.drawImage(img, 0, 0, width, height);

            const quality = isLossyFormat ? Math.max(0.45, 0.88 - attempt * 0.08) : undefined;
            let blob = await canvasToBlob(canvas, file.type, quality);
            if (!blob) break;
            let mimeType = file.type;

            // PNG can remain large even after resize; fallback to JPEG when needed.
            if (blob.size > MAX_UPLOAD_BYTES && file.type === "image/png") {
              const jpegQuality = Math.max(0.4, 0.8 - attempt * 0.08);
              const jpegBlob = await canvasToBlob(canvas, "image/jpeg", jpegQuality);
              if (jpegBlob && jpegBlob.size < blob.size) {
                blob = jpegBlob;
                mimeType = "image/jpeg";
              }
            }

            bestBlob = blob;
            bestMimeType = mimeType;
            if (blob.size <= MAX_UPLOAD_BYTES) {
              break;
            }

            width = Math.max(200, Math.round(width * RESIZE_STEP));
            height = Math.max(200, Math.round(height * RESIZE_STEP));
          }

          if (!bestBlob) {
            URL.revokeObjectURL(fileUrl);
            return;
          }

          // Do not send if still too large after optimization attempts.
          if (bestBlob.size > MAX_UPLOAD_BYTES) {
            showToastError(t('Toast.Error.Common'));
            URL.revokeObjectURL(fileUrl);
            return;
          }

          const outputName = file.name.replace(/\.(png|jpg|jpeg|webp)$/i, bestMimeType === "image/jpeg" ? ".jpg" : ".png");
          const resizedFile = new File([bestBlob], outputName, { type: bestMimeType });
          const formData = new FormData();
          formData.append('src', resizedFile);
          formData.append('image_type', type);
          if (ImageResponce && type !== ImageTypes.GALLERY) {
            formData.append('prev_image_src', String(ImageResponce?.src));
          }

          await uploadImage(formData);
          setImageUrl(URL.createObjectURL(resizedFile));
          URL.revokeObjectURL(fileUrl);
        };

        img.onerror = () => {
          URL.revokeObjectURL(fileUrl);
        };
      }
    } catch (error) {
      console.error('Error selecting an image:', error);
    }
  };

  return {
    imageUrl,
    imageLoading,
    ImageResponce,
    imageLoadingError,
    imageLoadingSuccess,
    handleFileInputChange,
  }
};

export default useUploadFile